// Bảng dòng thuộc tính (ITEMS §4). Chỉ dòng P0 được roll ngẫu nhiên; dòng P1 có mặt khi độc bản
// cố định dùng tới (vd Thần Thức của Trầm Uyên Kính). Khoảng ghi T1 → T5.
import { ELEMENT_IDS } from '../elements';
import type { AffixDef, SlotKind, StatKey, AffixUnit } from './types';
import type { Phase } from '../screens';

type R = readonly [number, number];
/** Khoảng viết theo tài liệu T5 → T1, lưu T1 → T5. */
const t5to1 = (...r: [R, R, R, R, R]): readonly R[] => [r[4], r[3], r[2], r[1], r[0]];

function a(
  id: string,
  slots: SlotKind[],
  unit: AffixUnit,
  ranges: readonly R[],
  stat: StatKey | null,
  roleSlots: SlotKind[] = [],
  phase: Phase = 'P0',
): AffixDef {
  return { id, slots, unit, ranges, stat, roleSlots, phase };
}

const ELEM_DMG = ELEMENT_IDS.map((el) =>
  a(
    `elemDmg.${el}`,
    ['weapon', 'necklace', 'treasure'],
    'pct',
    t5to1([3, 4], [4, 6], [6, 8], [8, 11], [11, 14]),
    null,
  ),
);
const RES = ELEMENT_IDS.map((el) =>
  a(
    `res.${el}`,
    ['helm', 'robe', 'pants', 'boots', 'ring'],
    'pct',
    t5to1([4, 6], [6, 9], [9, 12], [12, 16], [16, 20]),
    null,
  ),
);
const TRI = (['body', 'spirit', 'mind'] as const).map((k) =>
  a(
    `tri.${k}`,
    ['weapon', 'helm', 'robe', 'pants', 'gloves', 'boots', 'necklace', 'ring', 'treasure'],
    'xGiai',
    t5to1([0.4, 0.6], [0.6, 0.9], [0.9, 1.2], [1.2, 1.6], [1.6, 2.0]),
    k === 'body' ? 'triBody' : k === 'spirit' ? 'triSpirit' : 'triMind',
  ),
);

export const AFFIXES: readonly AffixDef[] = [
  // 4.1 Công
  a(
    'atkFlat',
    ['weapon', 'gloves', 'ring', 'necklace'],
    'xP',
    t5to1([0.8, 1.2], [1.2, 1.6], [1.6, 2.1], [2.1, 2.7], [2.7, 3.4]),
    'atkFlat',
    ['weapon', 'ring'],
  ),
  a('atkPct', ['weapon', 'ring'], 'pct', t5to1([2, 3], [3, 4.5], [4.5, 6], [6, 8], [8, 10]), 'atkPct', [
    'weapon',
  ]),
  a(
    'critRate',
    ['gloves', 'ring', 'necklace', 'weapon'],
    'pct',
    t5to1([1, 1.5], [1.5, 2.5], [2.5, 3.5], [3.5, 4.5], [4.5, 6]),
    'critRate',
    ['gloves'],
  ),
  a(
    'critDmg',
    ['weapon', 'necklace', 'ring'],
    'pct',
    t5to1([4, 6], [6, 9], [9, 12], [12, 16], [16, 20]),
    'critDmg',
    ['necklace'],
  ),
  a('atkSpeed', ['gloves', 'weapon'], 'pct', t5to1([1, 2], [2, 3], [3, 4.5], [4.5, 6], [6, 8]), null, [
    'gloves',
  ]),
  a(
    'skillDmg',
    ['helm', 'necklace', 'treasure'],
    'pct',
    t5to1([2, 3], [3, 5], [5, 7], [7, 9], [9, 12]),
    null,
    ['treasure'],
  ),
  ...ELEM_DMG,
  a('poiseDmg', ['weapon', 'gloves'], 'pct', t5to1([3, 5], [5, 7], [7, 10], [10, 13], [13, 16]), null),
  a('parryPoise', ['gloves', 'weapon'], 'pct', t5to1([4, 6], [6, 9], [9, 12], [12, 15], [15, 20]), null),
  a('bossDmg', ['weapon', 'necklace'], 'pct', t5to1([2, 3], [3, 4], [4, 6], [6, 8], [8, 10]), null),
  a('brokenDmg', ['weapon'], 'pct', t5to1([5, 8], [8, 12], [12, 16], [16, 20], [20, 25]), null),
  // 4.2 Thủ
  a(
    'hpFlat',
    ['robe', 'pants', 'helm', 'boots'],
    'xP',
    t5to1([8, 12], [12, 16], [16, 21], [21, 27], [27, 34]),
    'hpFlat',
    ['robe', 'pants'],
  ),
  a('hpPct', ['robe', 'pants'], 'pct', t5to1([2, 3], [3, 4.5], [4.5, 6], [6, 8], [8, 10]), 'hpPct', ['robe']),
  a(
    'defRating',
    ['robe', 'pants', 'helm', 'boots'],
    'pt',
    t5to1([3, 5], [5, 8], [8, 11], [11, 14], [14, 18]),
    'def',
    ['pants'],
  ),
  ...RES,
  a('dodgeIframe', ['boots'], 'ms', t5to1([10, 15], [15, 25], [25, 35], [35, 45], [45, 60]), null, ['boots']),
  a('dodgeRecharge', ['boots', 'pants'], 'pct', t5to1([3, 5], [5, 8], [8, 11], [11, 14], [14, 18]), null, [
    'boots',
  ]),
  a('parryWindow', ['gloves'], 'ms', t5to1([5, 8], [8, 12], [12, 16], [16, 20], [20, 25]), null, ['gloves']),
  a('killMoveReduce', ['robe', 'helm'], 'pct', t5to1([3, 5], [5, 7], [7, 9], [9, 12], [12, 15]), null, [
    'helm',
  ]),
  // 4.3 Tiện ích và idle
  a(
    'qiRate',
    ['necklace', 'ring', 'treasure'],
    'pct',
    t5to1([1, 2], [2, 3], [3, 4], [4, 5.5], [5.5, 7]),
    null,
    ['necklace'],
  ),
  a('idleLoot', ['ring', 'boots'], 'pct', t5to1([2, 3], [3, 5], [5, 7], [7, 9], [9, 12]), null),
  a(
    'offlineEff',
    ['necklace', 'treasure'],
    'pct',
    t5to1([0.3, 0.5], [0.5, 0.8], [0.8, 1.1], [1.1, 1.5], [1.5, 2]),
    null,
    ['treasure'],
  ),
  a('herbYield', ['helm', 'ring'], 'pct', t5to1([2, 3], [3, 5], [5, 7], [7, 9], [9, 12]), null),
  a('itemFind', ['ring', 'necklace'], 'pct', t5to1([1, 2], [2, 3], [3, 4], [4, 5], [5, 6]), null),
  a('stoneFind', ['gloves', 'boots'], 'pct', t5to1([3, 5], [5, 7], [7, 10], [10, 13], [13, 16]), null),
  a('cdr', ['helm', 'necklace'], 'pct', t5to1([1, 2], [2, 3], [3, 4], [4, 5], [5, 6]), null, ['helm']),
  a('manaMax', ['helm', 'robe'], 'pct', t5to1([3, 5], [5, 7], [7, 9], [9, 12], [12, 15]), null),
  a('manaRegen', ['necklace', 'treasure'], 'pct', t5to1([4, 6], [6, 9], [9, 12], [12, 16], [16, 20]), null),
  a('moveSpeed', ['boots'], 'pct', t5to1([2, 3], [3, 4], [4, 5], [5, 6], [6, 8]), null, ['boots']),
  ...TRI,
  // Dòng P1: chỉ xuất hiện khi độc bản cố định dùng tới / dòng gốc của phôi.
  a('divineSense', ['helm', 'treasure'], 'pt', t5to1([1, 1], [2, 2], [3, 3], [4, 4], [5, 6]), null, [], 'P1'),
  a('pillToxRes', ['necklace'], 'pct', t5to1([3, 5], [5, 7], [7, 9], [9, 12], [12, 15]), null, [], 'P1'),
];

/** Dòng chỉ làm dòng gốc (implicit) của phôi, không roll: Kháng mọi hệ. */
export const IMPLICIT_ONLY_AFFIXES = ['resAll'] as const;

export function affixDef(id: string): AffixDef {
  const d = AFFIXES.find((x) => x.id === id);
  if (!d) throw new RangeError(`unknown affix: ${id}`);
  return d;
}

export function maybeAffix(id: string): AffixDef | undefined {
  return AFFIXES.find((x) => x.id === id);
}

/** Dòng roll được ở ô này (P0). */
export function affixPool(slot: SlotKind): readonly AffixDef[] {
  return AFFIXES.filter((d) => d.phase === 'P0' && d.slots.includes(slot));
}
